<template>
	<BlockVisibilityObserver title="MAIN">
		<section class="kova-lp-promo">
			<!-- Lower slice of Ellipse 2974; the upper one is in LandingKovaHeader. Mobile only:
			     on desktop the same light moves inside the product card. -->
			<span aria-hidden="true" class="kova-lp-promo__glow" />

			<div class="kova-lp-promo__inner">
				<div class="kova-lp-promo__copy">
					<!-- Desktop only: on mobile the rating block sits inside the header. -->
					<div class="kova-lp-promo__rating">
						<div class="kova-lp-promo__rating-score">
							<LandingKovaStars class="kova-lp-promo__stars" />
							<p class="kova-lp-promo__rating-value">{{ rating.score }}&nbsp; {{ rating.label }}</p>
						</div>
						<p class="kova-lp-promo__rating-caption">{{ rating.caption }}</p>
					</div>

					<h1 class="kova-lp-promo__title">{{ title }}</h1>

					<div class="kova-lp-promo__price">
						<p class="kova-lp-promo__price-label">{{ price.label }}</p>
						<div class="kova-lp-promo__price-row">
							<p class="kova-lp-promo__price-now">{{ price.now }}</p>
							<div class="kova-lp-promo__price-aside">
								<p class="kova-lp-promo__price-was">{{ price.was }}</p>
								<p class="kova-lp-promo__price-period">{{ price.period }}</p>
							</div>
						</div>
					</div>

					<p class="kova-lp-promo__description">{{ description }}</p>
				</div>

				<!-- Source order is the frame's z-order: the card and everything printed on it
				     sit under the product shot, and only the delivery stamp goes over it. -->
				<div class="kova-lp-promo__visual">
					<!-- Desktop counterpart of __glow: on mobile Ellipse 2974 lights the whole
					     panel, on desktop it lives inside the product card. -->
					<span aria-hidden="true" class="kova-lp-promo__card-glow" />

					<!-- Desktop only: the mobile frame drops the stock badge. -->
					<p class="kova-lp-promo__badge">{{ product.badge }}</p>

					<ul class="kova-lp-promo__chips">
						<li v-for="feature in features" :key="feature" class="kova-lp-promo__chip">
							<InlineSvg class="kova-lp-promo__check" :src="checkIcon" />
							<span>{{ feature }}</span>
						</li>
					</ul>

					<!-- Each breakpoint gets its own shot: the compositions differ, not just the
					     crop. <source> keeps the browser from fetching the one it will not draw. -->
					<picture class="kova-lp-promo__shot-picture">
						<source media="(min-width: 1024px)" :srcset="product.desktop.image" />
						<img alt="" class="kova-lp-promo__shot" :src="product.mobile.image" :style="shotStyle" />
					</picture>

					<!-- Desktop only: on mobile the stamp rides the header instead of the card. -->
					<span aria-hidden="true" class="kova-lp-promo__stamp" />
				</div>

				<div class="kova-lp-promo__actions">
					<!-- Ellipse 2975 — dark vignette behind the CTA. Mobile only. -->
					<span aria-hidden="true" class="kova-lp-promo__vignette" />

					<button class="kova-lp-promo__cta" type="button" @click="onCtaClick">
						<span>{{ ctaLabel }}</span>
						<InlineSvg class="kova-lp-promo__cta-arrow" :src="arrowIcon" />
					</button>

					<div class="kova-lp-promo__promises">
						<p v-for="promise in promises" :key="promise.label" class="kova-lp-promo__promise">
							<span v-if="promise.icon === 'cross'" class="kova-lp-promo__promise-ring">
								<InlineSvg class="kova-lp-promo__cross" :src="crossIcon" />
							</span>
							<InlineSvg v-else class="kova-lp-promo__promise-check" :src="checkIcon" />
							<span>{{ promise.label }}</span>
						</p>
					</div>
				</div>
			</div>
		</section>

		<!-- divider / lines (6009:47970 mobile, 6009:49600 desktop) — certification ticker
		     below the green panel, so it sits outside the section: inside it would ride the
		     green and its rounded bottom. Both frames run the row past the edges of the
		     board, so it reads as a marquee; no timing was specified. -->
		<div class="kova-lp-promo__marquee">
			<div class="kova-lp-promo__marquee-track">
				<div v-for="pass in 2" :key="pass" class="kova-lp-promo__marquee-run" :aria-hidden="pass === 2">
					<template v-for="item in trust" :key="item.label">
						<span class="kova-lp-promo__trust">
							<img alt="" class="kova-lp-promo__trust-icon" :src="trustIcons[item.icon]" />
							<span>{{ item.label }}</span>
						</span>
						<span aria-hidden="true" class="kova-lp-promo__trust-dot">·</span>
					</template>
				</div>
			</div>
		</div>
	</BlockVisibilityObserver>
</template>

<script>
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import LandingKovaStars from '@/modules/Landing/components/LandingKovaStars.vue'

import arrowIcon from '@/assets/icons/ds_arrow_right.svg'
import checkIcon from '@/assets/kova/landing/check.svg'
import crossIcon from '@/assets/kova/landing/cross.svg'
import trustClinicians from '@/assets/kova/landing/trust-clinicians.svg'
import trustEligibility from '@/assets/kova/landing/trust-eligibility.svg'
import trustHipaa from '@/assets/kova/landing/trust-hipaa.svg'
import trustLegitscript from '@/assets/kova/landing/trust-legitscript.svg'

const trustIcons = {
	clinicians: trustClinicians,
	eligibility: trustEligibility,
	hipaa: trustHipaa,
	legitscript: trustLegitscript,
}

export default {
	name: 'LandingKovaHeroPromo',

	components: { BlockVisibilityObserver, LandingKovaStars },

	props: {
		ctaLabel: {
			type: String,
			required: true,
		},

		description: {
			type: String,
			required: true,
		},

		// Feature chips over the product shot — 5 in both frames.
		features: {
			type: Array,
			required: true,
		},

		// { label, now, was, period }
		price: {
			type: Object,
			required: true,
		},

		// { badge, mobile: { image, offsetRight, offsetTop, width }, desktop: { image, left, top, width } }
		// — the only per-medication part of the block. Each shot is the designer's export:
		// phone, vial(s) and shadow in one pre-rotated cut-out.
		product: {
			type: Object,
			required: true,
		},

		// [{ icon: 'cross' | 'check', label }] — the pair next to the CTA.
		promises: {
			type: Array,
			required: true,
		},

		// { score, label, caption } — desktop only; the mobile pill lives in the header.
		rating: {
			type: Object,
			required: true,
		},

		title: {
			type: String,
			required: true,
		},

		// [{ icon: keyof trustIcons, label }] — the certification marquee.
		trust: {
			type: Array,
			required: true,
		},
	},

	emits: ['progressLoaderCompleted'],

	setup() {
		return { arrowIcon, checkIcon, crossIcon, trustIcons }
	},

	computed: {
		// Mobile anchors the shot to the right edge in px — it is cropped to the 390px frame
		// and must keep that crop on any phone width. Desktop places it in % of the product
		// card, which scales with the grid column.
		shotStyle() {
			const { mobile, desktop } = this.product
			return {
				'--kova-lp-promo-shot-right': `${mobile.offsetRight}px`,
				'--kova-lp-promo-shot-top': `${mobile.offsetTop}px`,
				'--kova-lp-promo-shot-width': `${mobile.width}px`,
				'--kova-lp-promo-shot-left-lg': `${desktop.left}%`,
				'--kova-lp-promo-shot-top-lg': `${desktop.top}%`,
				'--kova-lp-promo-shot-width-lg': `${desktop.width}%`,
			}
		},
	},

	methods: {
		onCtaClick() {
			// Mapped to block MAIN by LANDING_CTA_BLOCK_NAMES in LandingView.
			this.$emit('progressLoaderCompleted', 'main_eligible')
		},
	},
}
</script>

<style lang="scss" scoped>
// Promo first screen. Mobile (Figma 5926:36623 / 5934:38663 / 5934:38432) continues the
// green panel the header starts; desktop (6009:49506 / 6009:50795 / 6009:52145) is a
// different composition — light page, two columns, green shrinks to the product card.
.kova-lp-promo {
	position: relative;
	overflow: hidden;

	padding-top: var(--size-32);

	font-family: var(--kova-lp-font);
	border-end-start-radius: var(--size-32);
	border-end-end-radius: var(--size-32);
	background: var(--kova-lp-hero-panel);
}

.kova-lp-promo__glow {
	position: absolute;
	left: 140px;
	top: -120px;
	width: 400px;
	height: 480px;

	border-radius: 50%;
	background: var(--kova-lp-hero-glow);
	filter: blur(80px);
}

// The column is flat, but the frames group it differently per breakpoint: mobile ties the
// title to the price at 20 and drops 24 before the description, desktop ties the rating to
// the title at 20 and keeps 28 everywhere else. Each breakpoint sets the common gap and
// the odd one out carries a margin.
.kova-lp-promo__copy {
	position: relative;
	display: flex;
	flex-direction: column;
	gap: var(--size-20);
	padding-inline: var(--size-16);
}

.kova-lp-promo__rating {
	display: none; // desktop only — see the header for the mobile placement
}

.kova-lp-promo__title {
	// The frame gives the headline the full 358 column and three 36px lines
	// (6009:47920 and 6009:49380 are both 358x108). A tighter 320 cap used to force those
	// same breaks back when the copy was shorter; once "GLP-1" joined the title it pushed
	// the longest variant onto a fourth line, which dropped the paragraph below into the
	// band where the vial is widest and clipped its last words.
	max-width: 358px;
	margin: 0;

	font-size: var(--size-32);
	font-weight: 500;
	line-height: var(--size-36);
	letter-spacing: -0.64px;

	color: var(--kova-lp-hero-on-panel);
}

.kova-lp-promo__price {
	display: flex;
	flex-direction: column;
	gap: var(--size-12);
}

.kova-lp-promo__price-label,
.kova-lp-promo__price-period {
	margin: 0;

	font-size: var(--size-16);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: -0.32px;

	color: var(--kova-lp-hero-accent);
}

.kova-lp-promo__price-row {
	display: flex;
	align-items: center;
	gap: var(--size-8);
}

.kova-lp-promo__price-now {
	margin: 0;

	font-size: var(--size-56);
	font-weight: 600;
	// Figma trims this box to the cap height, which is 39px at 56px Poppins. Matching
	// line-height reproduces it without relying on `text-box`, still Chromium/WebKit-only.
	line-height: 39px;
	letter-spacing: -1.12px;

	color: var(--kova-lp-hero-accent);
}

.kova-lp-promo__price-aside {
	display: flex;
	flex-direction: column;
	gap: var(--size-2);
}

.kova-lp-promo__price-was {
	margin: 0;

	font-size: var(--size-20);
	font-weight: 500;
	line-height: var(--size-20);

	color: var(--kova-lp-hero-on-panel-soft);
	text-decoration: line-through;
}

.kova-lp-promo__description {
	// The frame's 232 assumes the frame's own 390 width. The shot is pinned to the right
	// edge at a fixed size, so every pixel narrower than that slides it left into the
	// paragraph — at 360 it swallowed 32px of the third line. Shrinking the paragraph in
	// step keeps it clear, and anything from ~403 up gets the frame's number back. 171 is
	// the deepest the three shots reach, measured against their opaque pixels rather than
	// their boxes (the cut-outs carry a lot of transparent margin).
	max-width: min(232px, calc(100% - 171px));
	margin: var(--size-4) 0 0; // 20 + 4 = the 24 the frame puts above the paragraph

	font-size: var(--size-16);
	line-height: var(--size-22);

	color: var(--kova-lp-hero-on-panel);
}

// The wrapper exists only to carry the desktop <source>; it must not add a box of its
// own, or the absolutely-positioned shot would gain an empty line beside it.
.kova-lp-promo__shot-picture {
	display: contents;
}

.kova-lp-promo__shot {
	position: absolute;
	right: var(--kova-lp-promo-shot-right);
	top: var(--kova-lp-promo-shot-top);
	width: var(--kova-lp-promo-shot-width);
	height: auto;
}

.kova-lp-promo__badge,
.kova-lp-promo__card-glow,
.kova-lp-promo__stamp {
	display: none; // desktop only
}

.kova-lp-promo__chips {
	position: relative;

	display: flex;
	flex-direction: column;
	align-items: start;
	gap: var(--size-8);
	margin: var(--size-24) 0 0;
	padding-inline: var(--size-16);

	list-style: none;
}

.kova-lp-promo__chip {
	box-sizing: border-box;
	display: flex;
	align-items: center;
	gap: var(--size-6);
	padding: var(--size-8);

	font-size: var(--size-14);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: -0.14px;

	border-radius: var(--size-12);
	color: var(--kova-lp-hero-chip-ink);
	background: var(--kova-lp-hero-chip-bg);
	// Inset shadow rather than a border: Figma strokes sit inside the box, so a real
	// border would add 2px to every chip and drift the list.
	box-shadow: inset 0 0 0 1px var(--kova-lp-hero-chip-border);
	backdrop-filter: blur(100px);
}

// Figma stores icon-checkmark upside-down and icon-close as a plus, then flips /
// rotates them through wrapper transforms — mirrored here so the vectors stay untouched.
.kova-lp-promo__check {
	flex-shrink: 0;
	width: 11.81px;

	color: var(--kova-lp-hero-chip-ink);
	transform: scaleY(-1);
}

.kova-lp-promo__actions {
	position: relative;
	box-sizing: border-box;

	display: flex;
	flex-direction: column-reverse; // mobile puts the promises above the CTA
	align-items: center;
	gap: var(--size-16);
	margin-top: var(--size-16);
	padding: var(--size-16) var(--size-20) var(--size-20);
}

.kova-lp-promo__vignette {
	position: absolute;
	left: -145px;
	top: 44px;
	width: 664px;
	height: 209px;

	border-radius: 50%;
	background: var(--kova-lp-hero-shade);
	filter: blur(100px);
}

.kova-lp-promo__promises {
	position: relative;
	display: flex;
	justify-content: center;
	gap: var(--size-12);
}

.kova-lp-promo__promise {
	display: flex;
	align-items: center;
	gap: var(--size-4);
	margin: 0;

	font-size: var(--size-14);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: -0.14px;

	color: var(--kova-lp-hero-on-panel);
	white-space: nowrap;
}

.kova-lp-promo__promise-ring {
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	padding: 1px;

	border: 1px solid var(--kova-lp-check);
	border-radius: var(--size-48);
}

.kova-lp-promo__cross {
	width: var(--size-12);
	height: var(--size-12);

	color: var(--kova-lp-check);
	transform: scaleY(-1) rotate(45deg);
}

.kova-lp-promo__promise-check {
	flex-shrink: 0;
	width: 13.5px;

	color: var(--kova-lp-check);
	transform: scaleY(-1);
}

// Figma sets this one from the DS Button (Inter); every other CTA on the landing is
// Poppins, so it follows suit rather than switching face on resize.
.kova-lp-promo__cta {
	position: relative;
	box-sizing: border-box;

	display: flex;
	align-items: center;
	justify-content: center;
	gap: var(--size-4);
	width: 100%;
	max-width: 350px;
	height: var(--size-48);
	padding: var(--size-12);

	font-family: inherit;
	font-size: var(--size-16);
	font-weight: 600;
	line-height: var(--size-18);

	border: none;
	border-radius: var(--size-16);
	color: var(--kova-lp-hero-cta-ink);
	background: var(--kova-lp-hero-accent);
	cursor: pointer;
}

.kova-lp-promo__cta-arrow {
	flex-shrink: 0;
	width: var(--size-24);
	height: var(--size-24);
}

// Follows the green panel directly — its own padding is all the breathing room the row
// gets. Needs no background: the landing already paints --kova-lp-page.
.kova-lp-promo__marquee {
	position: relative;
	overflow: hidden;
	padding-block: var(--size-40);

	font-family: var(--kova-lp-font);
	// The fade is a fifth of the board on each side (300 of 1440), so it stays a
	// percentage rather than a fixed distance.
	mask-image: linear-gradient(to right, transparent, #000 20%, #000 80%, transparent);
}

.kova-lp-promo__marquee-track {
	display: flex;
	width: max-content;

	animation: kova-lp-promo-marquee 40s linear infinite;
}

.kova-lp-promo__marquee-run {
	display: flex;
	align-items: center;
	gap: var(--size-20);
	padding-left: var(--size-20);
}

.kova-lp-promo__trust {
	display: flex;
	align-items: center;
	gap: var(--size-6);

	font-size: var(--size-24);
	font-weight: 500;
	line-height: var(--size-34);

	color: var(--kova-lp-muted);
	white-space: nowrap;
}

.kova-lp-promo__trust-icon {
	width: var(--size-42);
	height: var(--size-42);
}

// The dot is set larger than the labels and is what gives the row its height
// (mobile frame: 128 = 40 + 48 + 40).
.kova-lp-promo__trust-dot {
	font-size: var(--size-34);
	font-weight: 500;
	line-height: var(--size-48);

	color: var(--kova-lp-muted);
}

@keyframes kova-lp-promo-marquee {
	to {
		transform: translateX(-50%);
	}
}

@media (prefers-reduced-motion: reduce) {
	.kova-lp-promo__marquee-track {
		animation: none;
	}
}

// Desktop (6009:49506) — light page, 566/674 columns, and the green panel shrinks to the
// product card. 1024px rather than the repo-wide 768px because the two columns collide
// below ~1280px of content width.
@media (min-width: 1024px) {
	.kova-lp-promo {
		overflow: visible; // the shot deliberately rises above the card
		padding-top: 0;

		border-radius: 0;
		background: none; // the landing's own --kova-lp-page shows through
	}

	.kova-lp-promo__glow {
		display: none;
	}

	.kova-lp-promo__inner {
		box-sizing: border-box;
		max-width: 1440px;
		margin-inline: auto;
		// The 782px hero row leaves 62px under its 720px card (6009:49528).
		padding: 0 var(--size-80) 62px;

		// 566 / 674 are the frame's two columns; as a ratio they hold their proportion
		// while the 80px gutters and the 40px gap stay put.
		display: grid;
		grid-template-columns: minmax(0, 566fr) minmax(0, 674fr);
		// The card spans both rows and is taller than the copy column. Left to auto rows
		// the grid would split that slack between them and push the CTA down, so the copy
		// row hugs and the actions row takes whatever the card leaves.
		grid-template-rows: max-content 1fr;
		gap: var(--size-40);
		align-items: start;
	}

	.kova-lp-promo__copy {
		grid-column: 1;
		grid-row: 1;
		gap: var(--size-28);
		padding: 0;
	}

	.kova-lp-promo__rating {
		align-self: start;

		display: flex;
		flex-direction: row;
		align-items: center;
		gap: var(--size-8);
		padding: var(--size-12) var(--size-16);

		border-radius: var(--size-16);
		background: var(--kova-lp-surface);
	}

	.kova-lp-promo__rating-score {
		display: flex;
		align-items: center;
		gap: var(--size-8);
	}

	.kova-lp-promo__stars {
		height: var(--size-16);
	}

	.kova-lp-promo__rating-value {
		margin: 0;

		font-size: var(--size-16);
		font-weight: 600;
		line-height: var(--size-22);

		color: var(--kova-lp-ink);
		white-space: nowrap;
	}

	.kova-lp-promo__rating-caption {
		margin: 0;

		font-size: var(--size-16);
		line-height: var(--size-22);

		color: var(--kova-lp-ink-70);
		white-space: nowrap;
	}

	.kova-lp-promo__title {
		// The rating pill and the title form one 20px-gap group inside the 28px column,
		// so the title carries the difference rather than a wrapper.
		margin-top: calc(var(--size-20) - var(--size-28));
		max-width: none;

		font-size: var(--size-52);
		line-height: 58px; // 1.12 at 52px — no token between 56 and 60
		letter-spacing: -1.04px;

		color: var(--kova-lp-text);
	}

	// Figma hugs this row at 339px; the parts size themselves to their own text, so a cap
	// is enough and the row can narrow with the column.
	.kova-lp-promo__price {
		max-width: 339px;
		gap: var(--size-20);
	}

	.kova-lp-promo__price-label,
	.kova-lp-promo__price-period {
		font-size: var(--size-24);
		// Literal on purpose: --size-26 is 1.715rem (27.44px) in _sizes.scss, the one
		// entry in the scale that is not n/16 rem. Six other screens already sit on that
		// value, so it is flagged rather than fixed here.
		line-height: 26px;
		letter-spacing: -0.48px;

		color: var(--kova-lp-brand);
	}

	.kova-lp-promo__price-row {
		align-items: end;
		gap: var(--size-16);
	}

	.kova-lp-promo__price-now {
		padding-block: var(--size-4);

		font-size: 92px; // 6009:49554 — between --size-88 and --size-96, kept exact
		line-height: var(--size-64); // cap height at 92px — see the mobile rule
		letter-spacing: -1.84px;

		color: var(--kova-lp-highlight);
	}

	.kova-lp-promo__price-aside {
		flex-shrink: 0;
		width: 124px;
		gap: var(--size-4);
	}

	.kova-lp-promo__price-was {
		font-size: var(--size-34);
		line-height: var(--size-48);

		color: var(--kova-lp-muted);
	}

	.kova-lp-promo__description {
		max-width: none;
		margin-top: 0; // desktop keeps the plain 28px column gap here

		font-size: var(--size-24);
		font-weight: 500;
		line-height: var(--size-36);

		color: var(--kova-lp-brand);
	}

	// section 1 (6009:50715) — the product card. Every child is placed in % of the
	// 674x720 frame so the card scales with the grid column.
	.kova-lp-promo__visual {
		position: relative;

		grid-column: 2;
		grid-row: 1 / span 2;
		aspect-ratio: 674 / 720;
		padding: 0;
	}

	// The card itself clips its own lighting; the shot and the stamp stay outside it so
	// the phone can rise above the top edge, as the frame draws it.
	.kova-lp-promo__visual::before {
		content: '';
		position: absolute;
		inset: 0;

		border-radius: var(--size-36);
		background: var(--kova-lp-hero-panel);
	}

	// Ellipse 2974 (6009:50717) — the light behind the shot, clipped to the card.
	.kova-lp-promo__card-glow {
		position: absolute;
		left: 21.513%;
		top: 1.528%;
		width: 62.017%;
		height: 92.222%;

		display: block;
		border-radius: 50%;
		background: var(--kova-lp-hero-glow);
		filter: blur(151px);
	}

	.kova-lp-promo__shot {
		right: auto;
		left: var(--kova-lp-promo-shot-left-lg);
		top: var(--kova-lp-promo-shot-top-lg);
		width: var(--kova-lp-promo-shot-width-lg);
	}

	// free delivery (6009:52141) — the stamp moves from the header onto the card.
	.kova-lp-promo__stamp {
		position: absolute;
		right: 4.748%;
		top: 4.444%;
		width: 26.706%;
		aspect-ratio: 1;

		display: block;
		background: url('@/assets/kova/landing/promo-badge-free-delivery.png') center / contain no-repeat;
	}

	.kova-lp-promo__badge {
		position: absolute;
		left: 4.748%;
		top: 4.444%;

		display: flex;
		align-items: center;
		gap: var(--size-10);
		margin: 0;
		padding: var(--size-10) var(--size-20);

		font-size: var(--size-24);
		line-height: var(--size-36);

		border-radius: var(--size-100);
		color: var(--kova-lp-hero-badge);
		box-shadow: inset 0 0 0 1px var(--kova-lp-hero-badge); // inside stroke, as in Figma
		white-space: nowrap;
	}

	.kova-lp-promo__badge::before {
		content: '';
		width: var(--size-10);
		height: var(--size-10);

		border-radius: 50%;
		background: var(--kova-lp-hero-accent);
	}

	// Anchored to the bottom of the card, not the top: at 1440 that is the same 538px
	// offset Figma draws, but on a narrower card the extra chip rows then grow upward
	// into the empty middle instead of being clipped.
	.kova-lp-promo__chips {
		position: absolute;
		left: 4.748%;
		bottom: 4.444%;
		width: 89.169%;
		margin: 0;
		padding: 0;

		flex-direction: row;
		flex-wrap: wrap;
		gap: var(--size-6);
	}

	.kova-lp-promo__chip {
		gap: var(--size-6);
		padding: var(--size-12) var(--size-14);

		font-size: var(--size-16);
		line-height: var(--size-22);
		letter-spacing: -0.16px;

		border-radius: var(--size-14);
	}

	.kova-lp-promo__actions {
		grid-column: 1;
		grid-row: 2;
		flex-direction: column; // desktop puts the CTA above the promises
		align-items: start;
		gap: var(--size-32);
		margin-top: 0; // the 40px to the copy above is the grid's own row gap
		padding: 0;
	}

	.kova-lp-promo__vignette {
		display: none;
	}

	.kova-lp-promo__cta {
		gap: var(--size-8);
		width: auto;
		max-width: 100%;
		height: 90px; // 6009:50644 — between --size-88 and --size-96, kept exact
		padding: 0 var(--size-60);

		font-size: var(--size-24);
		font-weight: 500;
		line-height: normal;

		border-radius: var(--size-28);
		color: var(--Fill-White);
		background: var(--kova-lp-brand);
	}

	.kova-lp-promo__cta-arrow {
		width: var(--size-32);
		height: var(--size-32);
	}

	.kova-lp-promo__promises {
		flex-wrap: wrap;
		gap: var(--size-16);
	}

	.kova-lp-promo__promise {
		gap: var(--size-8);

		font-size: var(--size-18);
		font-weight: 400;
		line-height: 26px; // see the price label — --size-26 is off by 1.44px
		letter-spacing: normal;

		color: var(--kova-lp-ink);
	}

	.kova-lp-promo__promise-ring {
		box-sizing: border-box;
		width: var(--size-24);
		height: var(--size-24);
		padding: var(--size-2);

		border-width: var(--size-2);
		border-radius: var(--size-80);
		border-color: var(--kova-lp-highlight);
	}

	.kova-lp-promo__cross {
		width: var(--size-18);
		height: var(--size-18);

		color: var(--kova-lp-highlight);
	}

	.kova-lp-promo__promise-check {
		width: 20.25px;

		color: var(--kova-lp-highlight);
	}

	// The second promise sits 4px from its icon while the first sits 8px (6009:49564).
	.kova-lp-promo__promise:last-child {
		gap: var(--size-4);
	}

	// lines (6009:49600) — same ticker, one size up.
	.kova-lp-promo__marquee {
		padding-block: var(--size-36);
	}

	.kova-lp-promo__marquee-run {
		gap: var(--size-28);
		padding-left: var(--size-28);
	}

	.kova-lp-promo__trust {
		font-size: var(--size-34);
	}
}
</style>
